import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { BannerCategoryBlock as BannerCategoryBlockV3 } from '~/components/isolated/CategoryBlocks/BannerCategoryBlock';
import { BannerMainBlock as BannerMainBlockV3 } from '~/components/isolated/CategoryBlocks/BannerMainBlock';
import { BannerPromoBlock as BannerPromoBlockV3 } from '~/components/isolated/CategoryBlocks/BannerPromoBlock';
import { CategoriesGrid2M } from '~/components/isolated/CategoryBlocks/CategoriesGrid2M';
import { CategoriesPromo } from '~/components/isolated/CategoryBlocks/CategoriesPromo';
import { CategoriesTiles } from '~/components/isolated/CategoryBlocks/CategoriesTiles';
import { ListOfCategories1 } from '~/components/isolated/CategoryBlocks/ListOfCategories1';
import { ProductsCarouselSliderAllHits } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderAllHits';
import { ProductsCarouselSliderOneBlock as ProductsCarouselSliderOneBlockV3 } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderOneBlock';
import { ProductsCarouselSliderSuperPrices } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderSuperPrices';
import { ProductsCarouselSliderWithBackground } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderWithBackground';
import { ProductsGridMultirow } from '~/components/isolated/CategoryBlocks/ProductsGridMultirow';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import BannerPromoBlock from './BanerPromoBlock/BannerPromoBlock';
import BannerCategoryBlock from './BannerCategoryBlock/BannerCategoryBlock';
import BannerLinkBlock from './BannerLinkBlock/BannerLinkBlock';
import BannerMainBlock from './BannerMainBlock/BannerMainBlock';
import BannerProductBlock from './BannerProductBlock/BannerProductBlock';
import CategoriesCarouselCircleBlock from './CategoriesCarouselCircleBlock/CategoriesCarouselCircleBlock';
import CategoriesCarouselSliderOneBlock from './CategoriesCarouselSliderOneBlock/CategoriesCarouselSliderOneBlock';
import CategoriesCarouselSliderThreeWBlock from './CategoriesCarouselSliderThreeWBlock/CategoriesCarouselSliderThreeWBlock';
import categoriesCarouselSliderThreeStyles from './CategoriesCarouselSliderThreeWBlock/CategoriesCarouselSliderThreeWBlock.module.scss';
import CategoriesCarouselSliderTwoSBlock from './CategoriesCarouselSliderTwoSBlock/CategoriesCarouselSliderTwoSBlock';
import CategoriesCarouselSquareThreeWBlock from './CategoriesCarouselSquareThreeWBlock/CategoriesCarouselSquareThreeWBlock';
import CategoriesGridOneSBlockFactory from './CategoriesGridOneSBlock/CategoriesGridOneSBlockFactory';
import CategoriesGridThreeMBlock from './CategoriesGridThreeMBlock/CategoriesGridThreeMBlock';
import CategoriesGridTwoMBlock from './CategoriesGridTwoMBlock/CategoriesGridTwoMBlock';
import { CategoriesTilesXSMultirow } from './CategoriesTilesXSMultirow';
import { CategoriesTilesXSScrollable } from './CategoriesTilesXSScrollable';
import CategoryBlockErrorBoundary from './CategoryBlockErrorBoundary';
import ListOfCategoriesDE from './ListOfCategoriesDE/ListOfCategoriesDE';
import ListOfCategoriesOneBlock from './ListOfCategoriesOneBlock/ListOfCategoriesOneBlock';
import ListOfCategoriesTwoBlock from './ListOfCategoriesTwoBlock/ListOfCategoriesTwoBlock';
import ProductsCarouselSliderOneBlock from './ProductsCarouselSliderOneBlock/ProductsCarouselSliderOneBlock';
import ProductsGridBlock from './ProductsGridBlock/ProductsGridBlock';

export enum CategoryDisplayType {
  MAIN_BANNER = 'main_banner',
  BANNER_CATEGORY = 'banner_category',
  PRODUCTS_CAROUSEL_SLIDER_ONE = 'products_carousel_slider_1',
  PRODUCTS_CAROUSEL_SLIDER_ALL_HITS = 'products_carousel_slider_all_hits',
  PRODUCTS_CAROUSEL_SLIDER_SUPER_PRICES = 'products_carousel_slider_super_prices',
  PRODUCTS_GRID_MULTIROW = 'products_grid_multirow',
  PRODUCTS_CAROUSEL_SLIDER_WITH_BACKGROUND = 'products_carousel_slider_with_background',
  BANNER_PRODUCT = 'banner_product',
  BANNER_LINK = 'banner_link',
  BANNER_PROMO = 'promo_banner',
  PRODUCTS_GRID = 'products_grid',
  LIST_OF_CATEGORIES_ONE = 'list_of_categories_1',
  LIST_OF_CATEGORIES_TWO = 'list_of_categories_2',
  CATEGORIES_CAROUSEL_SQUARE_ONE = 'categories_carousel_square',
  CATEGORIES_CAROUSEL_CIRCLE = 'categories_carousel_circle',
  CATEGORIES_CAROUSEL_SLIDER_ONE = 'categories_carousel_slider_1',
  CATEGORIES_CAROUSEL_SLIDER_TWO = 'categories_carousel_slider_2',
  CATEGORIES_CAROUSEL_SLIDER_THREE = 'categories_carousel_slider_3',
  CATEGORIES_FILES_SX_SCROLLABLE = 'categories_tiles_xs_scrollable',
  CATEGORIES_FILES_SX_MULTIROW = 'categories_tiles_xs_multirow',
  CATEGORIES_FILES_SX_SCROLLABLE_LVL_3 = 'categories_tiles_xs_scrollable_3_lvl',
  CATEGORIES_FILES_SX_MULTIROW_LVL_3 = 'categories_tiles_xs_multirow_3_lvl',
  CATEGORIES_GRID_ONE = 'categories_grid_1',
  CATEGORIES_GRID_ONE_UNMASKED = 'categories_grid_1_unmasked',
  CATEGORIES_GRID_TWO = 'categories_grid_2',
  CATEGORIES_PROMO = 'categories_promo',
  CATEGORIES_TILES_TWO_LEVEL = 'categories_tiles_2_lvl',
  CATEGORIES_TILES_THREE_LEVEL = 'categories_tiles_3_lvl',
  LIST_OF_CATEGORIES_HOME = 'list_of_categories_home',

  // TODO: Deprecated 👇🏻. Remove after database migration
  CATEGORIES_GRID_THREE = 'categories_grid_3_level',
}

interface UnknownCategoryProps {
  name: string;
  displayType: Nullable<string>;
}

const UnknownCategory = ({ name, displayType }: UnknownCategoryProps) => {
  return import.meta.env.DEV ? (
    <div>
      Here will be a block with name: {name} and displayType: {displayType}
    </div>
  ) : null;
};

const categoryToBanners = (data: Category[]) => {
  return data.map(({ name, previewImage, previewImageThumb, id }) => ({
    src: previewImage || '',
    scrThumb: previewImageThumb || '',
    alt: name,
    linkId: id,
  }));
};

const renderCategory = ({
  category,
  isMobile,
  externalLink,
}: {
  category: Category;
  isMobile: boolean;
  externalLink?: string;
}) => {
  switch (category.displayType) {
    case CategoryDisplayType.MAIN_BANNER: {
      const BannerMain = company.match({
        v3: BannerMainBlockV3 as typeof BannerMainBlock,
        default: BannerMainBlock,
      });
      return (
        <BannerMain
          banners={categoryToBanners(category.subcategory)}
          isMobile={isMobile}
        />
      );
    }
    case CategoryDisplayType.BANNER_CATEGORY: {
      const BannerCategory = company.match({
        v3: BannerCategoryBlockV3 as typeof BannerCategoryBlock,
        default: BannerCategoryBlock,
      });
      return (
        <BannerCategory
          banners={categoryToBanners(category.subcategory)}
          isMobile={isMobile}
        />
      );
    }
    case CategoryDisplayType.PRODUCTS_CAROUSEL_SLIDER_ONE: {
      const ProductsCarousel = company.match({
        v3: ProductsCarouselSliderOneBlockV3,
        default: ProductsCarouselSliderOneBlock,
      });
      return <ProductsCarousel category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_CAROUSEL_SLIDER_ALL_HITS: {
      return <ProductsCarouselSliderAllHits category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_CAROUSEL_SLIDER_SUPER_PRICES: {
      return <ProductsCarouselSliderSuperPrices category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_GRID_MULTIROW: {
      return <ProductsGridMultirow category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_CAROUSEL_SLIDER_WITH_BACKGROUND: {
      return <ProductsCarouselSliderWithBackground category={category} />;
    }
    case CategoryDisplayType.BANNER_PRODUCT: {
      return <BannerProductBlock category={category} />;
    }
    case CategoryDisplayType.BANNER_LINK: {
      return <BannerLinkBlock category={category} />;
    }
    case CategoryDisplayType.BANNER_PROMO: {
      const BannerPromo = company.match({
        v3: BannerPromoBlockV3,
        default: BannerPromoBlock,
      });
      return <BannerPromo category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_GRID: {
      return <ProductsGridBlock category={category} />;
    }
    case CategoryDisplayType.LIST_OF_CATEGORIES_ONE: {
      const ListOfCategories = company.match({
        v3: ListOfCategories1,
        default: ListOfCategoriesOneBlock,
      });
      return <ListOfCategories category={category} />;
    }
    case CategoryDisplayType.LIST_OF_CATEGORIES_TWO: {
      return <ListOfCategoriesTwoBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_ONE: {
      return <CategoriesCarouselSliderOneBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_THREE: {
      return <CategoriesCarouselSliderThreeWBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_SQUARE_ONE: {
      return <CategoriesCarouselSquareThreeWBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_CIRCLE: {
      return <CategoriesCarouselCircleBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_GRID_ONE: {
      return (
        <CategoriesGridOneSBlockFactory
          category={category}
          masked={true}
          externalLink={externalLink}
        />
      );
    }
    case CategoryDisplayType.CATEGORIES_GRID_ONE_UNMASKED: {
      return (
        <CategoriesGridOneSBlockFactory
          category={category}
          masked={false}
          externalLink={externalLink}
        />
      );
    }

    case CategoryDisplayType.CATEGORIES_GRID_TWO: {
      const CategoriesGrid2 = company.match({
        v3: CategoriesGrid2M,
        default: CategoriesGridTwoMBlock,
      });
      return <CategoriesGrid2 category={category} />;
    }
    // TODO: Deprecated 👇🏻. Remove after database migration
    case CategoryDisplayType.CATEGORIES_GRID_THREE:
    case CategoryDisplayType.CATEGORIES_TILES_TWO_LEVEL: {
      const CategoriesTiles2Lvl = company.match({
        [CompanyName.DubaiExpress]: CategoriesGridThreeMBlock,
        v3: CategoriesTiles as typeof CategoriesGridThreeMBlock,
        default: CategoriesGridThreeMBlock,
      });
      return <CategoriesTiles2Lvl category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_TILES_THREE_LEVEL: {
      const CategoriesTiles3Lvl = company.match({
        [CompanyName.DubaiExpress]: CategoriesGridThreeMBlock,
        v3: CategoriesTiles as typeof CategoriesGridThreeMBlock,
        default: CategoriesGridThreeMBlock,
      });
      return <CategoriesTiles3Lvl category={category} level={3} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_TWO: {
      return <CategoriesCarouselSliderTwoSBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_FILES_SX_SCROLLABLE: {
      return <CategoriesTilesXSScrollable category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_FILES_SX_SCROLLABLE_LVL_3: {
      return <CategoriesTilesXSScrollable category={category} level={3} />;
    }
    case CategoryDisplayType.CATEGORIES_FILES_SX_MULTIROW: {
      return <CategoriesTilesXSMultirow category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_FILES_SX_MULTIROW_LVL_3: {
      return <CategoriesTilesXSMultirow category={category} level={3} />;
    }
    case CategoryDisplayType.CATEGORIES_PROMO: {
      return <CategoriesPromo category={category} />;
    }
    case CategoryDisplayType.LIST_OF_CATEGORIES_HOME: {
      return <ListOfCategoriesDE category={category} />;
    }
    default:
      return (
        <UnknownCategory
          name={category.name}
          displayType={category.displayType}
        />
      );
  }
};

interface CategoryBlockProps {
  category: Category;
  externalLink?: string;
  className?: string;
}

const CategoryBlock = ({
  category,
  externalLink,
  className,
}: CategoryBlockProps) => {
  const { isMobile } = useGlobal();

  if (!category.subcategory.length) {
    return null;
  }

  return (
    <div
      className={classes([
        category.displayType ===
          CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_TWO &&
          categoriesCarouselSliderThreeStyles.rootWrapper,
        className,
      ])}
      data-display-type={category.displayType}
    >
      <CategoryBlockErrorBoundary parent={category?.displayType ?? 'unknown'}>
        {renderCategory({ category, isMobile, externalLink })}
      </CategoryBlockErrorBoundary>
    </div>
  );
};

export default observer(CategoryBlock);
